import { useEffect, useMemo, useState } from "react";
import { Badge, Card, EmptyState, PageHeader } from "@pericles/framework-frontend";
import { Link } from "react-router-dom";

import {
  approveProjectBrief,
  fetchProjectBrief,
  generateProjectBrief,
  regenerateProjectBriefSection,
  updateProjectBrief,
} from "../api/projectBriefClient";
import type {
  ProjectBriefContent,
  ProjectBriefRecord,
  ProjectBriefBullet,
  ProjectBriefSectionTarget,
} from "../types/projectBrief";
import { downloadProjectBriefMarkdown, downloadProjectBriefPdf } from "../utils/projectBriefExport";
import { parseSourceId, type ParsedSourceRef } from "../utils/projectBriefSources";

import { InlineAlert } from "./InlineAlert";

interface ProjectBriefPanelProps {
  projectId: string;
}

const STATUS_BADGE: Record<string, { label: string; variant: "default" | "success" } | undefined> = {
  DRAFT: { label: "Brouillon", variant: "default" },
  APPROVED: { label: "Approuvé", variant: "success" },
};

function formatUpdatedAt(value?: string | Date): string | null {
  if (!value) return null;
  const date = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium", timeStyle: "short" }).format(date);
}

function listToText(values?: string[]): string {
  return (values ?? []).join("\n");
}

function textToList(value: string): string[] {
  return value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

function bulletsToList(bullets?: ProjectBriefBullet[]): string[] {
  return (bullets ?? [])
    .map((bullet) => bullet.text?.trim())
    .filter((text): text is string => Boolean(text));
}

function listToBullets(values: string[], previous?: ProjectBriefBullet[]): ProjectBriefBullet[] {
  return values.map((text, index) => {
    const sourceIds = previous?.[index]?.sourceIds;

    return {
      text,
      ...(sourceIds?.length ? { sourceIds, sources: sourceIds } : {}),
    };
  });
}

function buildSourceHref(projectId: string, source: ParsedSourceRef): string {
  if (source.kind === "meeting") {
    return `/projects/${encodeURIComponent(projectId)}/meetings/${encodeURIComponent(source.id)}`;
  }

  if (source.kind === "document") {
    return `/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(source.id)}`;
  }

  return "#";
}

type ProjectBriefContentWithMetadata = ProjectBriefContent & {
  metadata?: { regeneratedSections?: string[] };
};

export function ProjectBriefPanel({ projectId }: ProjectBriefPanelProps): JSX.Element {
  const [brief, setBrief] = useState<ProjectBriefRecord | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [exportingFormat, setExportingFormat] = useState<"markdown" | "pdf" | null>(null);
  const [sectionStates, setSectionStates] = useState<
    Record<string, { isRegenerating: boolean; error?: string; locallyRegenerated?: boolean }>
  >({});

  const content: ProjectBriefContent | null = useMemo(() => brief?.content ?? null, [brief]);
  const isDraft = brief?.status === "DRAFT";

  const contentWithMetadata = useMemo(() => content as ProjectBriefContentWithMetadata | null, [content]);

  const regeneratedSectionIds = useMemo(() => {
    const ids = contentWithMetadata?.metadata?.regeneratedSections ?? [];
    return new Set(ids.filter((value): value is string => typeof value === "string"));
  }, [contentWithMetadata]);

  useEffect(() => {
    setBrief(null);
    setError(null);
    setSuccess(null);
    void loadBrief();
  }, [projectId]);

  useEffect(() => {
    setSectionStates({});
  }, [projectId]);

  async function loadBrief() {
    if (!projectId) return;
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const current = await fetchProjectBrief(projectId);
      setBrief(current);
    } catch (loadError: any) {
      setError(loadError?.message ?? "Failed to load project brief");
    } finally {
      setIsLoading(false);
    }
  }

  async function handleGenerate() {
    setIsGenerating(true);
    setError(null);
    setSuccess(null);
    try {
      const generated = await generateProjectBrief(projectId);
      setBrief(generated);
      setSuccess("Brief généré à partir de la discovery.");
    } catch (generateError: any) {
      setError(generateError?.message ?? "Failed to generate project brief");
      void loadBrief();
    } finally {
      setIsGenerating(false);
    }
  }

  async function handleApprove() {
    setIsApproving(true);
    setError(null);
    setSuccess(null);
    try {
      const approved = await approveProjectBrief(projectId);
      setBrief(approved);
      setSuccess("Brief approuvé.");
    } catch (approveError: any) {
      setError(approveError?.message ?? "Failed to approve project brief");
      void loadBrief();
    } finally {
      setIsApproving(false);
    }
  }

  async function handleExportMarkdown() {
    if (!content) {
      setError("Échec de l’export du brief en Markdown.");
      return;
    }

    setExportingFormat("markdown");
    setError(null);
    setSuccess(null);

    try {
      await downloadProjectBriefMarkdown({ brief: content, projectId, status: brief?.status });
      setSuccess("Export Markdown prêt.");
    } catch (exportError: any) {
      const message = exportError?.message ?? "Échec de l’export du brief en Markdown.";
      setError(message);
    } finally {
      setExportingFormat(null);
    }
  }

  async function handleExportPdf() {
    if (!content) {
      setError("Échec de l’export du brief en PDF. Veuillez réessayer.");
      return;
    }

    setExportingFormat("pdf");
    setError(null);
    setSuccess(null);

    try {
      await downloadProjectBriefPdf({ brief: content, projectId, status: brief?.status });
      setSuccess("Export PDF prêt au téléchargement.");
    } catch (exportError: any) {
      const message = exportError?.message ?? "Échec de l’export du brief en PDF. Veuillez réessayer.";
      setError(message);
    } finally {
      setExportingFormat(null);
    }
  }

  async function handleContentChange(nextContent: ProjectBriefContent) {
    if (!isDraft) return;

    setIsSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await updateProjectBrief(projectId, nextContent);
      setBrief(updated);
      setSuccess("Brief mis à jour.");
    } catch (saveError: any) {
      setError(saveError?.message ?? "Failed to save project brief");
      void loadBrief();
    } finally {
      setIsSaving(false);
    }
  }

  function mergeRegeneratedSection(
    current: ProjectBriefRecord | null,
    updated: ProjectBriefRecord,
    sectionId: string,
  ): ProjectBriefRecord {
    const fallback = current ?? updated;
    const updatedSection = updated.content.sections.find((section) => section.id === sectionId);
    const currentSections = fallback.content.sections ?? [];

    const mergedSections = updatedSection
      ? currentSections.some((section) => section.id === sectionId)
        ? currentSections.map((section) => (section.id === sectionId ? updatedSection : section))
        : [...currentSections, updatedSection]
      : currentSections;

    const metadata =
      (updated.content as ProjectBriefContentWithMetadata).metadata ??
      (current?.content as ProjectBriefContentWithMetadata | undefined)?.metadata;

    return {
      ...updated,
      content: {
        ...updated.content,
        ...(metadata ? { metadata } : {}),
        sections: mergedSections,
      },
    };
  }

  async function handleRegenerateSection(sectionId: string, target: ProjectBriefSectionTarget = "sections") {
    if (!sectionId || !isDraft) return;

    setSectionStates((previous) => ({
      ...previous,
      [sectionId]: { ...previous[sectionId], isRegenerating: true, error: undefined },
    }));
    setError(null);
    setSuccess(null);

    try {
      const updated = await regenerateProjectBriefSection(projectId, {
        target,
        sectionId,
        markRegenerated: true,
      });

      setBrief((current) => mergeRegeneratedSection(current, updated, sectionId));
      setSectionStates((previous) => ({
        ...previous,
        [sectionId]: {
          ...previous[sectionId],
          isRegenerating: false,
          error: undefined,
          locallyRegenerated: true,
        },
      }));
      setSuccess("Section régénérée.");
    } catch (regenerateError: any) {
      const message = regenerateError?.message ?? "Failed to regenerate project brief section";
      setSectionStates((previous) => ({
        ...previous,
        [sectionId]: { ...previous[sectionId], isRegenerating: false, error: message },
      }));
      setError(message);
      void loadBrief();
    }
  }

  function renderHeader(): JSX.Element {
    const badge = STATUS_BADGE[brief?.status ?? ""];
    const lastUpdated = formatUpdatedAt(brief?.updatedAt);
    return (
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <PageHeader
            title={content?.title?.trim() ? content.title : "Project Brief"}
            subtitle="Synthèse de la discovery en brief actionnable."
          />
          <div className="flex items-center gap-3 text-sm text-slate-600">
            {badge ? <Badge label={badge.label} variant={badge.variant} /> : null}
            {lastUpdated ? <span>Dernière mise à jour : {lastUpdated}</span> : null}
          </div>
        </div>
      </div>
    );
  }

  function renderEmpty(): JSX.Element {
    return (
      <Card>
        <EmptyState
          title="Aucun Project Brief pour le moment"
          description="Générez un brouillon à partir des éléments de discovery."
          action={
            <button
              type="button"
              className="button button-primary"
              onClick={handleGenerate}
              disabled={isGenerating}
            >
              {isGenerating ? "Génération en cours..." : "Générer depuis la discovery"}
            </button>
          }
        />
      </Card>
    );
  }

  function renderListEditor(
    label: string,
    values: string[] | undefined,
    onChange: (next: string[]) => void,
    placeholder?: string,
    disabled?: boolean,
  ): JSX.Element {
    if (!isDraft) {
      return (
        <div className="flex flex-col gap-1 text-sm text-slate-800">
          <span className="font-semibold">{label}</span>
          {values?.length ? (
            <ul className="list-disc space-y-1 pl-5 text-slate-700">
              {values.map((value, index) => (
                <li key={`${label}-${index}`}>{value}</li>
              ))}
            </ul>
          ) : (
            <p className="text-slate-500">Aucun élément.</p>
          )}
        </div>
      );
    }

    return (
      <label className="flex flex-col gap-1 text-sm text-slate-800">
        <span className="font-semibold">{label}</span>
        <textarea
          className="input"
          disabled={disabled ?? (!isDraft || isSaving)}
          placeholder={placeholder ?? "Une entrée par ligne"}
          value={listToText(values)}
          onChange={(event) => onChange(textToList(event.target.value))}
          rows={3}
        />
      </label>
    );
  }

  function renderSectionEditor(index: number): JSX.Element | null {
    if (!content?.sections?.[index]) return null;
    const section = content.sections[index];
    const sectionId = section.id;
    const sectionState = sectionStates[sectionId] ?? { isRegenerating: false };
    const isRegenerating = sectionState.isRegenerating;
    const showRegenerated = regeneratedSectionIds.has(sectionId) || sectionState.locallyRegenerated;
    const isSectionDisabled = !isDraft || isSaving || isRegenerating;

    const bulletsContent = section.bullets?.length ? (
      <ul className="list-disc space-y-1 pl-4 text-sm text-slate-700">
        {section.bullets.map((bullet, bulletIndex) => (
          <li key={`${section.id ?? index}-bullet-${bulletIndex}`}>{bullet.text}</li>
        ))}
      </ul>
    ) : (
      <p className="text-sm text-slate-500">Aucun point clé.</p>
    );

    return (
      <Card key={section.id ?? index}>
        <div className="flex flex-col gap-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex flex-col gap-1">
              <p className="text-xs uppercase tracking-wide text-slate-500">Section</p>
              {isDraft ? (
                <input
                  className="input"
                  disabled={isSectionDisabled}
                  value={section.title ?? ""}
                  onChange={(event) => {
                    const nextSections = [...(content?.sections ?? [])];
                    nextSections[index] = { ...section, title: event.target.value };
                    void handleContentChange({ ...(content as ProjectBriefContent), sections: nextSections });
                  }}
                />
              ) : (
                <h3 className="text-lg font-semibold text-slate-800">{section.title || "Sans titre"}</h3>
              )}
              {showRegenerated ? (
                <span className="inline-flex w-fit items-center gap-1 rounded-full bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800">
                  Régénéré par l’IA
                </span>
              ) : null}
              {section.sourceIds?.length ? (
                <div className="text-xs text-slate-500">
                  Basé sur{" "}
                  {section.sourceIds.map((raw, idx) => {
                    const parsed = parseSourceId(raw as any);
                    const label =
                      parsed.kind === "meeting"
                        ? `Réunion #${parsed.id}`
                        : parsed.kind === "document"
                          ? `Document #${parsed.id}`
                          : typeof raw === "string"
                            ? raw
                            : "Source";

                    if (parsed.kind === "meeting" || parsed.kind === "document") {
                      return (
                        <span key={`${section.id}-${parsed.kind}-${parsed.id}`}>
                          {idx > 0 ? ", " : null}
                          <Link className="text-blue-600 hover:underline" to={buildSourceHref(projectId, parsed)}>
                            {label}
                          </Link>
                        </span>
                      );
                    }

                    return (
                      <span key={`${section.id}-source-${idx}`}>
                        {idx > 0 ? ", " : null}
                        {label}
                      </span>
                    );
                  })}
                </div>
              ) : null}
            </div>
            {isDraft ? (
              <div className="flex flex-col items-end gap-2">
                <button
                  type="button"
                  className="button button-secondary button-sm"
                  disabled={isSectionDisabled}
                  onClick={() => void handleRegenerateSection(sectionId)}
                >
                  {isRegenerating ? "Régénération…" : "Régénérer la section"}
                </button>
                {sectionState.error ? (
                  <span className="text-xs text-red-600">{sectionState.error}</span>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="flex flex-col gap-2">
            <label className="flex flex-col gap-1 text-sm text-slate-800">
              <span className="font-semibold">Résumé</span>
              {isDraft ? (
                <textarea
                  className="input"
                  disabled={isSectionDisabled}
                  value={section.summary ?? ""}
                  onChange={(event) => {
                    const nextSections = [...(content?.sections ?? [])];
                    nextSections[index] = { ...section, summary: event.target.value };
                    void handleContentChange({ ...(content as ProjectBriefContent), sections: nextSections });
                  }}
                  rows={3}
                />
              ) : (
                <p className="whitespace-pre-line text-sm text-slate-700">{section.summary || "Aucun résumé."}</p>
              )}
            </label>

            <label className="flex flex-col gap-1 text-sm text-slate-800">
              <span className="font-semibold">Détails</span>
              {isDraft ? (
                <textarea
                  className="input"
                  disabled={isSectionDisabled}
                  value={section.details ?? ""}
                  onChange={(event) => {
                    const nextSections = [...(content?.sections ?? [])];
                    nextSections[index] = { ...section, details: event.target.value };
                    void handleContentChange({ ...(content as ProjectBriefContent), sections: nextSections });
                  }}
                  rows={4}
                />
              ) : (
                <p className="whitespace-pre-line text-sm text-slate-700">{section.details || "Aucun détail."}</p>
              )}
            </label>

            {isDraft
              ? renderListEditor("Points clés", bulletsToList(section.bullets), (next) => {
                  const nextSections = [...(content?.sections ?? [])];
                  nextSections[index] = { ...section, bullets: listToBullets(next, section.bullets) };
                  void handleContentChange({ ...(content as ProjectBriefContent), sections: nextSections });
                }, undefined, isSectionDisabled)
              : (
                  <div className="flex flex-col gap-1 text-sm text-slate-800">
                    <span className="font-semibold">Points clés</span>
                    {bulletsContent}
                  </div>
                )}
          </div>
        </div>
      </Card>
    );
  }

  function renderBody(): JSX.Element {
    if (!brief || !content) {
      return renderEmpty();
    }

    return (
      <div className="flex flex-col gap-4">
        <Card>
          <div className="flex flex-col gap-3">
            {isDraft ? (
              <InlineAlert
                tone="warning"
                title="Brouillon"
                description="Ce brief peut encore être modifié avant validation."
              />
            ) : (
              <InlineAlert tone="success" title="Brief approuvé" description="Contenu en lecture seule." />
            )}
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                className="button button-secondary button-sm"
                disabled={isGenerating || !isDraft}
                onClick={handleGenerate}
              >
                {isGenerating ? "Régénération…" : "Régénérer avec l'IA"}
              </button>

              <button
                type="button"
                className="button button-primary button-sm"
                disabled={!isDraft || isApproving}
                onClick={handleApprove}
              >
                {isApproving ? "Validation…" : "Approuver le brief"}
              </button>

              <button
                type="button"
                className="button button-secondary button-sm"
                disabled={Boolean(exportingFormat)}
                onClick={() => void handleExportMarkdown()}
              >
                {exportingFormat === "markdown" ? "Export markdown…" : "Exporter en Markdown"}
              </button>

              <button
                type="button"
                className="button button-secondary button-sm"
                disabled={Boolean(exportingFormat)}
                onClick={() => void handleExportPdf()}
              >
                {exportingFormat === "pdf" ? "Export PDF…" : "Exporter en PDF"}
              </button>

              {isSaving ? <InlineAlert tone="info" title="Enregistrement…" /> : null}
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex flex-col gap-4">
            <div className="grid gap-4 md:grid-cols-2">
              <label className="flex flex-col gap-1 text-sm text-slate-800">
                <span className="font-semibold">Titre</span>
                {isDraft ? (
                  <input
                    className="input"
                    disabled={!isDraft || isSaving}
                    value={content.title ?? ""}
                    onChange={(event) =>
                      void handleContentChange({ ...(content as ProjectBriefContent), title: event.target.value })
                    }
                  />
                ) : (
                  <p className="text-base font-semibold text-slate-800">{content.title || "Sans titre"}</p>
                )}
              </label>

              <label className="flex flex-col gap-1 text-sm text-slate-800">
                <span className="font-semibold">Résumé</span>
                {isDraft ? (
                  <textarea
                    className="input"
                    disabled={!isDraft || isSaving}
                    value={content.summary ?? ""}
                    onChange={(event) =>
                      void handleContentChange({ ...(content as ProjectBriefContent), summary: event.target.value })
                    }
                    rows={3}
                  />
                ) : (
                  <p className="whitespace-pre-line text-sm text-slate-700">{content.summary || "Aucun résumé."}</p>
                )}
              </label>
            </div>

            <label className="flex flex-col gap-1 text-sm text-slate-800">
              <span className="font-semibold">Vue d'ensemble</span>
              {isDraft ? (
                <textarea
                  className="input"
                  disabled={!isDraft || isSaving}
                  value={content.overview ?? ""}
                  onChange={(event) =>
                    void handleContentChange({ ...(content as ProjectBriefContent), overview: event.target.value })
                  }
                  rows={4}
                />
              ) : (
                <p className="whitespace-pre-line text-sm text-slate-700">{content.overview || "Aucune vue d'ensemble."}</p>
              )}
            </label>

            <div className="grid gap-4 md:grid-cols-2">
              {renderListEditor("Objectifs", content.goals, (next) =>
                void handleContentChange({ ...(content as ProjectBriefContent), goals: next }),
              )}

              {renderListEditor("Périmètre", content.scope, (next) =>
                void handleContentChange({ ...(content as ProjectBriefContent), scope: next }),
              )}

              {renderListEditor("Hors périmètre", content.nonGoals, (next) =>
                void handleContentChange({ ...(content as ProjectBriefContent), nonGoals: next }),
              )}

              {renderListEditor("Contraintes", content.constraints, (next) =>
                void handleContentChange({ ...(content as ProjectBriefContent), constraints: next }),
              )}

              {renderListEditor("Risques", content.risks, (next) =>
                void handleContentChange({ ...(content as ProjectBriefContent), risks: next }),
              )}

              {renderListEditor("Recommandations", content.recommendations, (next) =>
                void handleContentChange({ ...(content as ProjectBriefContent), recommendations: next }),
              )}

              {renderListEditor("Prochaines étapes", content.nextSteps, (next) =>
                void handleContentChange({ ...(content as ProjectBriefContent), nextSteps: next }),
              )}
            </div>

            <div className="flex flex-col gap-1 text-sm text-slate-800">
              <span className="font-semibold">Notes additionnelles</span>
              {isDraft ? (
                <textarea
                  className="input"
                  disabled={!isDraft || isSaving}
                  value={content.markdown ?? ""}
                  onChange={(event) =>
                    void handleContentChange({ ...(content as ProjectBriefContent), markdown: event.target.value })
                  }
                  rows={4}
                  placeholder="Markdown libre pour clarifier le brief"
                />
              ) : (
                <p className="whitespace-pre-line text-sm text-slate-700">{content.markdown || "Aucune note."}</p>
              )}
            </div>
          </div>
        </Card>

        <div className="flex flex-col gap-3">
          <PageHeader title="Sections" subtitle="Les blocs générés par l'assistant." />
          {content.sections?.length ? (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {content.sections.map((_section, index) => renderSectionEditor(index))}
            </div>
          ) : (
            <InlineAlert tone="info" title="Aucune section générée pour le moment." />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {renderHeader()}
      <div aria-live="polite" className="flex flex-col gap-2">
        {error ? <InlineAlert tone="danger" title={error} /> : null}
        {success ? <InlineAlert tone="success" title={success} /> : null}
      </div>
      {isLoading ? <InlineAlert tone="info" title="Chargement du brief..." /> : renderBody()}
    </div>
  );
}
